import {
    getFirestore, collection, query, getDocs, orderBy,
    updateDoc, deleteDoc, doc, where, serverTimestamp,
    addDoc, limit
} from "firebase/firestore";
import { app } from "../firebase-config.js";
import { uploadImage } from "./storage-service.js"; // Reuse existing storage service
import { PlazaService } from "./plaza.service.js";

const db = getFirestore(app);

export const AdminCurationService = {
    // --- Banner Management ---

    getBanners: async () => {
        const q = query(collection(db, 'banners'), orderBy('order', 'asc'));
        const snap = await getDocs(q);
        return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    },

    /**
     * Uploads image and creates banner doc
     * @param {Blob} imageBlob - WebP or compressed blob
     * @param {number} order - Display order
     */
    addBanner: async (imageBlob, order) => {
        const fileName = `banners/${Date.now()}_optimized.webp`;
        try {
            const imageUrl = await uploadImage(imageBlob, fileName);
            await addDoc(collection(db, 'banners'), {
                imageUrl,
                linkUrl: '',
                order: order,
                createdAt: serverTimestamp()
            });
            return true;
        } catch (e) {
            console.error("Add Banner Error:", e);
            throw e;
        }
    },

    updateBanner: async (id, data) => {
        await updateDoc(doc(db, 'banners', id), data);
    },

    deleteBanner: async (id) => {
        await deleteDoc(doc(db, 'banners', id));
    },

    // --- Curation (Today's Picks) ---

    getCuratedSeries: async () => {
        // We fetch all curated series
        const q = query(collection(db, 'series'), where('isCurated', '==', true));
        const snap = await getDocs(q);
        // Sort by priority (asc)
        return snap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .sort((a, b) => (a.priority || 999) - (b.priority || 999));
    },

    addCuration: async (seriesId, priority) => {
        try {
            console.log(`[Service] Adding curation: ${seriesId}, priority: ${priority}`);
            await updateDoc(doc(db, 'series', seriesId), {
                isCurated: true,
                priority
            });
            console.log(`[Service] Curation added successfully.`);
            return true;
        } catch (e) {
            console.error("Add Curation Error:", e);
            throw e;
        }
    },

    removeCuration: async (seriesId) => {
        await updateDoc(doc(db, 'series', seriesId), {
            isCurated: false,
            priority: 999
        });
    },

    async reorderCuration(itemA, itemB, newPriorityA, newPriorityB) {
        // itemA -> newPriorityA
        // itemB -> newPriorityB
        await Promise.all([
            updateDoc(doc(db, 'series', itemA.id), { priority: newPriorityA }),
            updateDoc(doc(db, 'series', itemB.id), { priority: newPriorityB })
        ]);
    },

    // --- AI Analysis / Recommendations ---

    /**
     * Fetches candidates from multiple pools to ensure diversity
     * Pool A: Recent (Viral New)
     * Pool B: Popular (All-time Hits)
     * Pool C: Loved (Hidden Gems)
     */
    getCandidates: async (poolSize = 30) => {
        try {
            // Parallel Fetching for Performance
            const [recentSnap, popularSnap, lovedSnap] = await Promise.all([
                getDocs(query(collection(db, 'series'), orderBy('createdAt', 'desc'), limit(poolSize))),
                getDocs(query(collection(db, 'series'), orderBy('viewCount', 'desc'), limit(poolSize))),
                getDocs(query(collection(db, 'series'), orderBy('likeCount', 'desc'), limit(poolSize)))
            ]);

            // Merge and Deduplicate by ID
            const combinedMap = new Map();

            const addToMap = (doc) => {
                if (!combinedMap.has(doc.id)) {
                    combinedMap.set(doc.id, { id: doc.id, ...doc.data() });
                }
            };

            recentSnap.forEach(addToMap);
            popularSnap.forEach(addToMap);
            lovedSnap.forEach(addToMap);

            return Array.from(combinedMap.values());
        } catch (e) {
            console.error("Candidate Fetch Error:", e);
            return [];
        }
    },

    /**
     * Helper to filter Rising Stars
     * Logic: High views/likes relative to age, or just high momentum
     */
    filterRisingStars: (candidates, curatedIds) => {
        return candidates
            .filter(s => !curatedIds.has(s.id))
            // Must have some traction
            .filter(s => {
                const views = s.views || s.viewCount || 0;
                const likes = s.likes || s.likeCount || 0;
                return views > 100 || likes > 20;
            })
            // Sort by View Count (Descending) to find the "Hottest"
            .sort((a, b) => {
                const vA = a.views || a.viewCount || 0;
                const vB = b.views || b.viewCount || 0;
                return vB - vA;
            })
            .slice(0, 5); // Increased from 3 to 5
    },

    /**
     * Helper to filter Hidden Gems
     * Logic: High engagement rate (Likes / Views) but not necessarily massive views yet
     */
    filterHiddenGems: (candidates, curatedIds) => {
        return candidates
            .filter(s => !curatedIds.has(s.id))
            // "Hidden": Not already super famous (e.g., < 5000 views) but loved
            .filter(s => {
                const views = s.views || s.viewCount || 0;
                const likes = s.likes || s.likeCount || 0;
                // Avoid division by zero
                if (views === 0) return false;

                const ratio = likes / views;
                // Criteria: Less than 5k views AND (> 5% like ratio OR > 10 likes)
                return views < 5000 && (ratio > 0.05 || likes > 10);
            })
            // Sort by Like Count (Descending) to find the "Most Loved" among the hidden
            .sort((a, b) => {
                const lA = a.likes || a.likeCount || 0;
                const lB = b.likes || b.likeCount || 0;
                return lB - lA;
            })
            .slice(0, 5); // Increased from 3 to 5
    },

    // --- Social / Plaza Integration ---

    postPlazaFeed: async (feedData) => {
        try {
            await PlazaService.createPost({
                ...feedData,
                isAdminContent: true,
                authorId: feedData.authorId || 'system',
                authorName: feedData.authorName || '운영 알림',
                hashtags: feedData.hashtags || [],
                imageUrls: feedData.imageUrls || []
            });
            return true;
        } catch (e) {
            console.error("Plaza Post Error:", e);
            throw e;
        }
    }
};
